import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ClockTimerIcon } from 'components/Icons/ClockTimerIcon'
import { DotsIcon } from 'components/Icons/DotsIcon'
import Dropdown from 'components/v2/Dropdown'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { IKreditActionsProps } from 'features/Kredits/views/Index/Actions/types'
import { DEPOSIT_KREDITS_BONUS_FUNDS, DEPOSIT_KREDIT_CORPORATE_FUNDS } from 'features/Kredits/mutations-legacy'
import { useMutation } from '@apollo/client'
import Modal from 'components/v2/Modal'
import styled from 'styled-components'
import { WarningIcon } from 'components/Icons'
import { CoinIcon } from 'components/Icons/CoinIcon'
import { sanitizePrice } from 'utils/math'
import { DepositKreditsForm } from 'features/Kredits/components/DepositKreditsForm'
import { canDepositKreditsCorporateFund } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GET_KREDITS } from 'features/Kredits/queries-legacy'
import { IDepositKreditsFormValues } from 'features/Kredits/types'
import { GTKreditAccountType } from 'gql/types.generated'

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
`

export const KreditActions = ({ kredit, isDisabled }: IKreditActionsProps): ReactElement => {
  const login = useLogin()
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isDepositKreditsModalOpen, setIsDepositKreditsModalOpen] = useState(false)
  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const [depositKredits, { error: depositKreditsError, loading: depositKreditsLoading }] = useMutation(
    DEPOSIT_KREDIT_CORPORATE_FUNDS,
    {
      onCompleted: (): void => {
        setIsDepositKreditsModalOpen(false)
      },
      refetchQueries: ['GetKredits']
    }
  )

  const [depositBonusKredits, { error: depositBonusKreditsError, loading: depositBonusKreditsLoading }] = useMutation(
    DEPOSIT_KREDITS_BONUS_FUNDS,
    {
      onCompleted: (): void => {
        setIsDepositKreditsModalOpen(false)
      },
      onError: (): void => {
        // Do nothing
      },
      refetchQueries: [GET_KREDITS]
    }
  )

  const isDepositKreditsDisabled = !canDepositKreditsCorporateFund(login.accessToken.policies)
  // TODO uncomment when backend is ready
  // !canDepositKreditsCorporateFund(login.accessToken.policies) || !kredit.businessClient?.isKreditEnabled

  const onSubmit = (values: IDepositKreditsFormValues): void => {
    switch (values.creditType) {
      case GTKreditAccountType.Corporate:
        depositKredits({
          variables: {
            input: {
              account: {
                kreditID: kredit.id,
                userAccountID: kredit.user.id
              },
              amount: sanitizePrice(Number(values.amount) * 100, true)
            }
          }
        })
        break
      case GTKreditAccountType.Bonus:
        depositBonusKredits({
          variables: {
            input: {
              accounts: [
                {
                  kreditID: kredit.id,
                  userAccountID: kredit.user.id
                }
              ],
              amount: sanitizePrice(Number(values.amount) * 100, true)
            }
          }
        })
        break
    }
  }
  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton onClick={toggleDropdown} icon={<DotsIcon />} noBorder smallPadding disabled={isDisabled} />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.Item action noPadding>
              <List.Link isAction to={`/kredits/${kredit.id}/transactions`}>
                <List.Icon>
                  <ClockTimerIcon />
                </List.Icon>
                <FormattedMessage id="kredits.actions.transactions.label" defaultMessage="View history" />
              </List.Link>
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (!isDepositKreditsDisabled) {
                  setIsDepositKreditsModalOpen(true)
                }
              }}
              disabled={isDepositKreditsDisabled}
            >
              <List.Icon>
                <CoinIcon />
              </List.Icon>
              <FormattedMessage id="kredits.actions.addCredits" defaultMessage="Add Credits" />
              {depositKreditsError || depositBonusKreditsError ? <StyledWarningIcon /> : null}
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isDepositKreditsModalOpen ? (
        <Modal.Container
          title={<FormattedMessage id="kredits.actions.depositKreditsModal.title" defaultMessage="Deposit Kredits" />}
          onClose={(): void => setIsDepositKreditsModalOpen(false)}
        >
          <DepositKreditsForm
            onSubmit={onSubmit}
            error={depositKreditsError || depositBonusKreditsError}
            onCancel={(): void => setIsDepositKreditsModalOpen(false)}
            loading={depositKreditsLoading || depositBonusKreditsLoading}
          />
        </Modal.Container>
      ) : null}
    </>
  )
}
